import React, { useState, useEffect, useMemo } from 'react';
import { Form, Button, Row, Col, Alert, Spinner, Card, Table } from 'react-bootstrap';
import axios from 'axios';
import { LocationFormData, Location, FullEquipment, Category, Subcategory, Consumable } from '../interfaces';
import MessageModal from './MessageModal';
import _ from 'lodash';
// אייקונים
import { 
  faSave, 
  faTimes,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { API_BASE_URL, buildApiUrl } from '../config/api';

interface LocationFormProps {
  locationToEdit?: Location | null;
  onSuccess: () => void;
  onCancel: () => void;
}

const groupEquipment = (equipment: FullEquipment[]) => {
    const grouped: { [category: string]: { [subcategory: string]: FullEquipment[] } } = {};
    equipment.forEach(item => {
        const category = item.category_name || 'Uncategorized';
        const subcategory = item.subcategory_name || 'Uncategorized';
        if (!grouped[category]) grouped[category] = {};
        if (!grouped[category][subcategory]) grouped[category][subcategory] = [];
        grouped[category][subcategory].push(item);
    });
    return grouped;
};

const LocationForm: React.FC<LocationFormProps> = ({ locationToEdit, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState<LocationFormData>({
    location_name: '',
    location_type: null,
    address: null,
    notes: null,
    recommended_equipment_data: [],
  });

  const [isIndoorLocation, setIsIndoorLocation] = useState<boolean>(false);

  const [allEquipment, setAllEquipment] = useState<FullEquipment[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);

  const [selectedCategory, setSelectedCategory] = useState<number | ''>('');
  const [filteredSubcategories, setFilteredSubcategories] = useState<Subcategory[]>([]);
  const [selectedSubcategory, setSelectedSubcategory] = useState<number | ''>('');
  const [availableEquipment, setAvailableEquipment] = useState<FullEquipment[]>([]);
  const [selectedEquipmentId, setSelectedEquipmentId] = useState<number | ''>('');
  const [recommendedEquipmentQuantity, setRecommendedEquipmentQuantity] = useState<number>(1);
  const [recommendedItems, setRecommendedItems] = useState<{ [key: number]: string }>({});
  const [allConsumables, setAllConsumables] = useState<Consumable[]>([]);
  const [recommendedConsumables, setRecommendedConsumables] = useState<{ [key: number]: string }>({});
  const [selectedConsumableId, setSelectedConsumableId] = useState<number | ''>('');
  const [recommendedConsumableQuantity, setRecommendedConsumableQuantity] = useState<number>(1);

  const [error, setError] = useState<string | null>(null);
  const [loadingDeps, setLoadingDeps] = useState<boolean>(true);
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);

  // תיקון: העברתי את ה-useMemo לראש הקומפוננטה
  const groupedInventory = useMemo(() => groupEquipment(allEquipment), [allEquipment]);
  
  const currentRecommendedItemsForDisplay = useMemo(() => {
    return Object.entries(recommendedItems)
        .map(([id, qty]) => {
            const item = allEquipment.find(eq => eq.id === Number(id));
            if (!item || item.id === undefined) return null;
            return { id: item.id, name: item.name, quantity: Number(qty) };
        })
        .filter(Boolean);
  }, [recommendedItems, allEquipment]);

  const currentRecommendedConsumablesForDisplay = useMemo(() => {
    return Object.entries(recommendedConsumables)
      .map(([id, qty]) => {
        const c = allConsumables.find((x) => x.id === Number(id));
        if (!c || c.id === undefined) return null;
        return { id: c.id, name: c.name, quantity: Number(qty) };
      })
      .filter(Boolean);
  }, [recommendedConsumables, allConsumables]);


  useEffect(() => {
    const fetchDependencies = async () => {
      try {
        const [equipmentRes, categoriesRes, subcategoriesRes, consumablesRes] = await Promise.all([
          axios.get(buildApiUrl('inventory/equipment/')),
          axios.get(buildApiUrl('settings/categories/')),
          axios.get(buildApiUrl('settings/subcategories/')),
          axios.get<Consumable[]>(buildApiUrl('settings/consumables/')),
        ]);
        setAllEquipment(equipmentRes.data);
        setCategories(categoriesRes.data);
        setSubcategories(subcategoriesRes.data);
        setAllConsumables(consumablesRes.data);
      } catch (err) {
        setError("לא ניתן לטעון נתונים נדרשים לטופס.");
      } finally {
        setLoadingDeps(false);
      }
    };
    fetchDependencies();
  }, []);

  useEffect(() => {
    if (locationToEdit) {
      setFormData({
        id: locationToEdit.id,
        location_name: locationToEdit.location_name || '',
        location_type: locationToEdit.location_type || null,
        address: locationToEdit.address || null,
        notes: locationToEdit.notes || null,
        recommended_equipment_data: [],
      });
      
      // בדיקה אם המיקום הוא בפנים או בחוץ לפי סוג המיקום
      const locationType = locationToEdit.location_type || '';
      setIsIndoorLocation(locationType.includes('פנים') || locationType.includes('סגור') || locationType.includes('indoor'));
      
      const initialQuantities: { [key: number]: string } = {};
      if (locationToEdit.recommended_equipment_details) {
        locationToEdit.recommended_equipment_details.forEach(item => {
          if (item.equipment) {
            initialQuantities[item.equipment] = String(item.quantity);
          }
        });
      }
      setRecommendedItems(initialQuantities);

      const initialCons: { [key: number]: string } = {};
      if (locationToEdit.recommended_consumable_details) {
        locationToEdit.recommended_consumable_details.forEach((item) => {
          if (item.consumable) {
            initialCons[item.consumable] = String(item.quantity);
          }
        });
      }
      setRecommendedConsumables(initialCons);
    } else {
      setFormData({
        location_name: '',
        location_type: null,
        address: null,
        notes: null,
        recommended_equipment_data: [],
      });
      setRecommendedItems({});
      setRecommendedConsumables({});
      setSelectedConsumableId('');
      setIsIndoorLocation(false);
    }
  }, [locationToEdit]);

  useEffect(() => {
    if (selectedCategory) {
      setFilteredSubcategories(subcategories.filter(sub => sub.category === Number(selectedCategory)));
      setSelectedSubcategory('');
    } else {
      setFilteredSubcategories([]);
      setSelectedSubcategory('');
    }
  }, [selectedCategory, subcategories]);

  useEffect(() => {
    if (selectedSubcategory) {
      const filtered = allEquipment.filter(eq => 
        eq.subcategory === Number(selectedSubcategory)
      );
      setAvailableEquipment(filtered);
      setSelectedEquipmentId('');
    } else {
      setAvailableEquipment([]);
      setSelectedEquipmentId('');
    }
  }, [selectedSubcategory, allEquipment]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value === '' ? null : value });
  };
  
  const handleQuantityChange = (equipmentId: number, value: string) => {
    setRecommendedItems(prev => ({...prev, [equipmentId]: value}));
  };
  
  const handleAddRecommendedItem = () => {
    if (selectedEquipmentId && recommendedEquipmentQuantity > 0) {
      const itemToAdd = allEquipment.find(eq => eq.id === Number(selectedEquipmentId));
      if (itemToAdd && itemToAdd.id !== undefined) {
        setRecommendedItems(prev => {
          const newItems = {...prev};
          newItems[itemToAdd.id!] = String(Number(newItems[itemToAdd.id!] || 0) + recommendedEquipmentQuantity);
          return newItems;
        });
        setSelectedCategory('');
        setSelectedSubcategory('');
        setSelectedEquipmentId('');
        setRecommendedEquipmentQuantity(1);
        setError(null);
      }
    } else {
      setError("אנא בחר/י פריט והזן/י כמות חוקית.");
    }
  };

  const handleRemoveRecommendedItem = (equipmentId: number) => {
    setRecommendedItems(prev => {
        const newItems = {...prev};
        delete newItems[equipmentId];
        return newItems;
    });
  };

  const handleAddRecommendedConsumable = () => {
    if (selectedConsumableId && recommendedConsumableQuantity > 0) {
      const c = allConsumables.find((x) => x.id === Number(selectedConsumableId));
      if (c && c.id !== undefined) {
        setRecommendedConsumables((prev) => {
          const next = { ...prev };
          next[c.id!] = String(Number(next[c.id!] || 0) + recommendedConsumableQuantity);
          return next;
        });
        setSelectedConsumableId('');
        setRecommendedConsumableQuantity(1);
        setError(null);
      }
    } else {
      setError('אנא בחר/י מתכלה והזן/י כמות חוקית.');
    }
  };

  const handleRemoveRecommendedConsumable = (consumableId: number) => {
    setRecommendedConsumables((prev) => {
      const next = { ...prev };
      delete next[consumableId];
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitLoading(true);

    if (!formData.location_name) {
      setError("שם המיקום הוא שדה חובה.");
      setSubmitLoading(false);
      return;
    }
    
    // בנה את ה-payload לשליחה
    const dataToSend: any = { 
      ...formData,
      location_type: isIndoorLocation ? 'פנים (מקום סגור)' : 'חוץ (מקום פתוח)',
      recommended_equipment_data: Object.entries(recommendedItems)
        .filter(([, qty]) => Number(qty) > 0)
        .map(([id, qty]) => ({ equipment: Number(id), quantity: Number(qty) })),
      recommended_consumable_data: Object.entries(recommendedConsumables)
        .filter(([, qty]) => Number(qty) > 0)
        .map(([id, qty]) => ({ consumable: Number(id), quantity: Number(qty) })),
    };
    
    // מחיקת שדות שלא קיימים במודל כדי למנוע שגיאות
    delete dataToSend.recommended_equipment_details;
    delete dataToSend.recommended_consumable_details;
    
    try {
      if (locationToEdit?.id) {
        await axios.put(buildApiUrl(`settings/locations/${locationToEdit.id}/`), dataToSend);
      } else {
        await axios.post(buildApiUrl('settings/locations/'), dataToSend);
      }
      onSuccess();
    } catch (err) {
      console.error("שגיאה בשליחת טופס מיקום:", err);
      if (axios.isAxiosError(err) && err.response && err.response.data) {
        let errorMessages = '';
        for (const key in err.response.data) {
          if (Object.prototype.hasOwnProperty.call(err.response.data, key)) {
            const errorValue = err.response.data[key];
            if (Array.isArray(errorValue)) {
              errorMessages += `${key}: ${errorValue.join(', ')}\n`;
            } else {
              errorMessages += `${key}: ${errorValue}\n`;
            }
          }
        }
        setError(`שגיאה בשמירת המיקום: \n${errorMessages || 'שגיאה לא ידועה.'}`);
      } else {
        setError("שגיאה בשמירת המיקום. אנא נסה שוב.");
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit} className="p-4 border rounded shadow-sm bg-light">
      <h3 className="mb-4">{locationToEdit ? 'עריכת מיקום' : 'הוספת מיקום חדש'}</h3>
      {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}

      <Row className="mb-3">
        <Form.Group as={Col} controlId="formLocationName">
          <Form.Label>שם מיקום</Form.Label>
          <Form.Control type="text" name="location_name" value={formData.location_name} onChange={handleChange} required />
        </Form.Group>
        <Form.Group as={Col} controlId="formIndoorLocation">
          <Form.Check
            type="checkbox"
            label="מיקום בפנים (במקום סגור)"
            checked={isIndoorLocation}
            onChange={(e) => setIsIndoorLocation(e.target.checked)}
          />
        </Form.Group>
      </Row>
      <Form.Group className="mb-3" controlId="formAddress">
        <Form.Label>כתובת</Form.Label>
        <Form.Control as="textarea" rows={3} name="address" value={formData.address || ''} onChange={handleChange} />
      </Form.Group>
      <Form.Group className="mb-3" controlId="formNotes">
        <Form.Label>הערות</Form.Label>
        <Form.Control as="textarea" rows={3} name="notes" value={formData.notes || ''} onChange={handleChange} />
      </Form.Group>
      
      <Card className="mt-4 mb-4">
        <Card.Header>ניהול ציוד מומלץ למיקום</Card.Header>
        <Card.Body>
           <Row className="mb-3">
            <Col md={4}>
              <Form.Group controlId="compCategory">
                <Form.Label>קטגוריה</Form.Label>
                <Form.Select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(parseInt(e.target.value) || '')}
                  disabled={loadingDeps}
                >
                  <option value="">בחר קטגוריה...</option>
                  {categories.map(cat => (
                    <option key={`comp-cat-${cat.id}`} value={cat.id}>
                      {cat.category_name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group controlId="compSubcategory">
                <Form.Label>תת-קטגוריה</Form.Label>
                <Form.Select
                  value={selectedSubcategory}
                  onChange={(e) => setSelectedSubcategory(parseInt(e.target.value) || '')}
                  disabled={loadingDeps || !selectedCategory}
                >
                  <option value="">בחר תת-קטגוריה...</option>
                  {filteredSubcategories.map(subcat => (
                    <option key={`comp-sub-${subcat.id}`} value={subcat.id}>
                      {subcat.subcategory_name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group controlId="compEquipment">
                <Form.Label>פריט ציוד</Form.Label>
                <Form.Select
                  value={selectedEquipmentId}
                  onChange={(e) => setSelectedEquipmentId(parseInt(e.target.value) || '')}
                  disabled={loadingDeps || !selectedSubcategory}
                >
                  <option value="">בחר פריט...</option>
                  {availableEquipment.map(item => (
                    <option key={`comp-equip-${item.id}`} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group controlId="compQuantity">
                <Form.Label>כמות נדרשת</Form.Label>
                <Form.Control
                  type="number"
                  value={recommendedEquipmentQuantity}
                  onChange={(e) => setRecommendedEquipmentQuantity(parseInt(e.target.value) || 1)}
                  min="1"
                  disabled={!selectedEquipmentId}
                />
              </Form.Group>
            </Col>
            <Col md={6} className="d-flex align-items-end">
              <Button
                variant="outline-primary"
                onClick={handleAddRecommendedItem}
                disabled={!selectedEquipmentId || recommendedEquipmentQuantity <= 0}
                className="w-100"
              >
                הוסף ציוד מומלץ
              </Button>
            </Col>
          </Row>
          {currentRecommendedItemsForDisplay.length > 0 && (
            <div className="mt-4">
              <h6>פריטים מומלצים שנבחרו:</h6>
              <Table striped bordered hover size="sm" responsive>
                <thead>
                  <tr>
                    <th>שם פריט</th>
                    <th>כמות</th>
                    <th>פעולות</th>
                  </tr>
                </thead>
                <tbody>
                  {currentRecommendedItemsForDisplay.map(item => {
                    if (!item) return null;
                    return (
                      <tr key={item.id}>
                        <td>{item.name}</td>
                        <td>{item.quantity}</td>
                        <td>
                                                          <Button variant="danger" size="sm" onClick={() => handleRemoveRecommendedItem(item.id)}>
                                    <FontAwesomeIcon icon={faTrash} className="me-1" />
                                    הסר
                                </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      <Card className="mt-4 mb-4">
        <Card.Header>ניהול מתכלים מומלצים למיקום</Card.Header>
        <Card.Body>
          <Row className="mb-3">
            <Col md={8}>
              <Form.Group controlId="locConsumable">
                <Form.Label>מתכלה</Form.Label>
                <Form.Select
                  value={selectedConsumableId === '' ? '' : String(selectedConsumableId)}
                  onChange={(e) => setSelectedConsumableId(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                  disabled={loadingDeps}
                >
                  <option value="">בחר מתכלה...</option>
                  {[...allConsumables]
                    .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'he'))
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group controlId="locConsumableQty">
                <Form.Label>כמות</Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  value={recommendedConsumableQuantity}
                  onChange={(e) => setRecommendedConsumableQuantity(parseInt(e.target.value, 10) || 1)}
                  disabled={!selectedConsumableId}
                />
              </Form.Group>
            </Col>
          </Row>
          <Button
            variant="outline-primary"
            className="mb-3"
            onClick={handleAddRecommendedConsumable}
            disabled={!selectedConsumableId || recommendedConsumableQuantity <= 0}
          >
            הוסף מתכלה מומלצת
          </Button>
          {currentRecommendedConsumablesForDisplay.length > 0 && (
            <Table striped bordered hover size="sm" responsive>
              <thead>
                <tr>
                  <th>שם</th>
                  <th>כמות</th>
                  <th>פעולות</th>
                </tr>
              </thead>
              <tbody>
                {currentRecommendedConsumablesForDisplay.map((item) => {
                  if (!item) return null;
                  return (
                    <tr key={item.id}>
                      <td>{item.name}</td>
                      <td>{item.quantity}</td>
                      <td>
                        <Button variant="danger" size="sm" onClick={() => handleRemoveRecommendedConsumable(item.id)}>
                          <FontAwesomeIcon icon={faTrash} className="me-1" />
                          הסר
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
      
      <div className="d-flex justify-content-end">
                                <Button variant="secondary" onClick={onCancel} className="me-2" disabled={submitLoading}>
                            <FontAwesomeIcon icon={faTimes} className="me-1" />
                            ביטול
                        </Button>
                        <Button variant="primary" type="submit" disabled={submitLoading}>
                            {submitLoading ? <Spinner animation="border" size="sm" /> : <><FontAwesomeIcon icon={faSave} className="me-1" /> {locationToEdit ? 'עדכן מיקום' : 'הוסף מיקום'}</>}
                        </Button>
      </div>
    </Form>
  );
};

export default LocationForm;
